import React from 'react';
import { i18n } from 'config/locales';
import ValidationElement from 'components/Form/ValidationElement';
import { ValidationElementProps } from 'components/Form/ValidationElement/ValidationElement';
import {
  CommonOnUpdate,
  CommonOnError,
  CommonScrollIntoView,
  CommonRequired,
  CommonAddressBooks,
} from 'types/props';
import { Dispatch } from 'redux';
import { ErrorCode } from 'redux/modules/application/reducers/error';
import { LegalTechnologyManipulatingItemModel } from 'forms/sections/legal/technology/manipulating/types';
import Field from 'components/Form/Field';
import DateControl from 'components/Form/DateControl';
import Textarea from 'components/Form/Textarea';
import Location from 'components/Form/Location';
// PVQ imports
import * as formTypes from 'constants/formTypes';
import { isPVQForm } from 'helpers/form';
import PVQLocation from 'components/Form/PVQLocation';
import Text from 'components/Form/Text';
import NotApplicable from 'components/Form/NotApplicable';
import Branch from 'components/Form/Branch';
import Dropdown from 'components/Form/Dropdown';
import PVQName from 'components/Form/PVQName';
import { systemITTypes, systemITLocationOptions, handingProtectedInformationFrequencyOptions } from 'constants/enums/informationTechnologyOptions';
import MultipleDropdown from 'components/Form/MultipleDropdown';
import CheckboxGroup from 'components/Form/CheckboxGroup';
import Checkbox from 'components/Form/Checkbox';
import Actions from './Actions';

interface Props extends Partial<LegalTechnologyManipulatingItemModel>, ValidationElementProps {
  onUpdate?: CommonOnUpdate
  onError?: CommonOnError
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  addressBooks?: CommonAddressBooks
  dispatch?: Dispatch
}

export default class ManipulatingItem extends ValidationElement<Props> {
  update = (queue: Partial<LegalTechnologyManipulatingItemModel>) => {
    this.props.onUpdate({
      Date: this.props.Date,
      Incident: this.props.Incident,
      Location: this.props.Location,
      Action: this.props.Action,
      ManipulatingLocations: this.props.ManipulatingLocations,
      SystemName: this.props.SystemName,
      SystemOwnerNotApplicable: this.props.SystemOwnerNotApplicable,
      SystemOwner: this.props.SystemOwner,
      InformationType: this.props.InformationType,
      InformationTypeOtherExplanation: this.props.InformationTypeOtherExplanation,
      HadSuccess: this.props.HadSuccess,
      Frequency: this.props.Frequency,
      FrequencyOtherExplanation: this.props.FrequencyOtherExplanation,
      Occurances: this.props.Occurances,
      OccurancesExplanation: this.props.OccurancesExplanation,
      UnauthorizedActions: this.props.UnauthorizedActions,
      ActionDetails: this.props.ActionDetails,
      ActionOtherDetails: this.props.ActionOtherDetails,
      Reason: this.props.Reason,
      ...queue,
    });
  };

  updateDate = (values: LegalTechnologyManipulatingItemModel['Date']) => {
    this.update({
      Date: values,
    });
  };

  updateIncident = (values: LegalTechnologyManipulatingItemModel['Incident']) => {
    this.update({
      Incident: values,
    });
  };

  updateLocation = (values: LegalTechnologyManipulatingItemModel['Location']) => {
    this.update({
      Location: values,
    });
  };

  updateAction = (values: LegalTechnologyManipulatingItemModel['Action']) => {
    this.update({
      Action: values,
    });
  };

  // PVQ Updates
  updateManipulatingLocations = (values: LegalTechnologyManipulatingItemModel['ManipulatingLocations']) => {
    const list = Checkbox.select(values, this.props.ManipulatingLocations);
    const includesOther = list.includes('Other');

    this.update({
      ManipulatingLocations: { values: list },
      Incident: includesOther ? this.props.Incident : {}
    });
  };

  updateUnauthorizedActions = (values: LegalTechnologyManipulatingItemModel['UnauthorizedActions']) => {
    this.update({
      UnauthorizedActions: values,
    });
  };

  updateActionDetails = (values: LegalTechnologyManipulatingItemModel['ActionDetails']) => {
    this.update({
      ActionDetails: values,
    });
  };

  updateActionOtherDetails = (values: LegalTechnologyManipulatingItemModel['ActionOtherDetails']) => {
    this.update({
      ActionOtherDetails: values,
    });
  };

  updateSystemName = (values: LegalTechnologyManipulatingItemModel['SystemName']) => {
    this.update({
      SystemName: values,
    });
  };

  updateSystemOwner = (values: LegalTechnologyManipulatingItemModel['SystemOwner']) => {
    this.update({
      SystemOwner: values,
    });
  };

  updateSystemOwnerNotApplicable = (value: LegalTechnologyManipulatingItemModel['SystemOwnerNotApplicable']) => {
    this.update({
      SystemOwner: value.applicable ? this.props.SystemOwner : {},
      SystemOwnerNotApplicable: value,
    });
  };

  updateInformationType = (values: LegalTechnologyManipulatingItemModel['InformationType']) => {
    this.update({
      InformationType: values,
    });
  };

  updateInformationTypeOtherExplanation = (values: LegalTechnologyManipulatingItemModel['InformationTypeOtherExplanation']) => {
    this.update({
      InformationTypeOtherExplanation: values,
    });
  };

  updateOccurances = (values: LegalTechnologyManipulatingItemModel['Occurances']) => {
    this.update({
      Occurances: values,
    });
  };

  updateOccurancesExplanation = (values: LegalTechnologyManipulatingItemModel['OccurancesExplanation']) => {
    this.update({
      OccurancesExplanation: values,
    });
  };

  updateFrequency = (values: LegalTechnologyManipulatingItemModel['Frequency']) => {
    this.update({
      Frequency: values,
    });
  };

  updateReason = (values: LegalTechnologyManipulatingItemModel['Reason']) => {
    this.update({
      Reason: values,
    });
  };

  updateFrequencyOtherExplanation = (values: LegalTechnologyManipulatingItemModel['FrequencyOtherExplanation']) => {
    this.update({
      FrequencyOtherExplanation: values,
    });
  };

  updateHadSuccess = (values: LegalTechnologyManipulatingItemModel['HadSuccess']) => {
    const additionalUpdates = values?.value && values.value !== 'Yes' ? {
      InformationType: { value: '' },
      InformationTypeOtherExplanation: {},
    } : {};
    this.update({
      HadSuccess: values,
      ...additionalUpdates,
    });
  };

  render() {
    const attribute = `List.items[${this.props.$index}].Item`;
    const isPVQ = isPVQForm(this.props.formType);
    const actionother = Array.isArray(this.props.UnauthorizedActions?.values) && this.props.UnauthorizedActions.values.includes('Other');
    const pvqPrefix = `legal.pvq.technology.manipulating`;

    return isPVQ ? (
      <div>
        <Field
          title={i18n.t(`${pvqPrefix}.heading.date`)}
          adjustFor="datecontrol"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.Date`}
        >
          <DateControl
            name="Date"
            {...this.props.Date}
            minDateEqualTo
            onUpdate={this.updateDate}
            onError={this.props.onError}
            className="Date"
            required={this.props.required}
            hideDay
            attribute={`${attribute}.Date`}
          />
        </Field>
        <Field
          title={i18n.t(`${pvqPrefix}.heading.incidentplace`)}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.ManipulatingLocations`}
        >
          <CheckboxGroup
            className="manipulating-information-access-location option-list option-list-vertical"
            attribute={`${attribute}.ManipulatingLocations`}
            onError={this.handleError}
            selectedValues={this.props.ManipulatingLocations?.values}
            required={this.props.required}
          >
            {systemITLocationOptions.map((s) => (
              <Checkbox
                key={s}
                name={s}
                label={s}
                value={s}
                onUpdate={this.updateManipulatingLocations}
                onError={this.props.onError}
              />
            ))}
          </CheckboxGroup>

          {
          this.props.ManipulatingLocations?.values?.includes('Other') && (

            <Textarea
              name="Incident"
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              {...this.props.Incident}
              onUpdate={this.updateIncident}
              onError={this.props.onError}
              className="Incident"
              required={this.props.required}
              attribute={`${attribute}.Incident`}
            />

          )
    }
        </Field>
        <Field
          attribute={`${attribute}.Location`}
          optional
          onError={this.props.onError}
          adjustFor="address"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <PVQLocation
            name="pvqlocaton"
            {...this.props.Location}
            label={i18n.t(`${pvqPrefix}.heading.inUS`)}
            title={i18n.t(`${pvqPrefix}.heading.inUS`)}
            domesticTitlel={i18n.t(`${pvqPrefix}.heading.usaddress`)}
            foreignTitlel={i18n.t(`${pvqPrefix}.heading.nonusaddress`)}
            onUpdate={this.updateLocation}
            onError={this.props.onError}
            className="pvqlocation"
            layout={PVQLocation.PVQ_DOMESTIC_FOREIGN_MILITARY_DIPLOMATIC_LOCATION}
            geocode
            showPostOffice
            addressBooks={this.props.addressBooks}
            addressBook="Incident"
            dispatch={this.props.dispatch}
            required={this.props.required}
            attribute={`${attribute}.Location`}
            showMilitaryInstallation
          />
        </Field>
        <Field
          title={i18n.t(`${pvqPrefix}.heading.systemname`)}
          adjustFor="labels"
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SystemName`}
        >
          <Text
            name="system-name"
            {...this.props.SystemName}
            className="system-name"
            maxlength="100"
            onUpdate={this.updateSystemName}
            onError={this.props.onError}
            required={this.props.required}
            attribute={`${attribute}.SystemName`}
          />
        </Field>

        <Field
          title={i18n.t(`${pvqPrefix}.heading.systemowner`)}
          scrollIntoView={this.props.scrollIntoView}
          attribute={`${attribute}.SystemOwner`}
        >
          <NotApplicable
            name="system-owner"
            label={i18n.t(`${pvqPrefix}.heading.idk`)}
            or={i18n.t(`${pvqPrefix}.heading.or`)}
            {...this.props.SystemOwnerNotApplicable}
            onUpdate={this.updateSystemOwnerNotApplicable}
            className="system-owner"
            required={this.props.required}
            onError={this.props.onError}
            attribute={`${attribute}.SystemOwnerNotApplicable`}
            includeNAConfirmation={Boolean(this.props.SystemOwner?.value)}
          >
            <Text
              name="legal-pvq-technology-unauthorized-sysowner"
              className="legal-pvq-technology-unauthorized-sysowner-na"
              {...this.props.SystemOwner}
              onError={this.props.onError}
              onUpdate={this.updateSystemOwner}
              required={this.props.required}
              attribute={`${attribute}.SystemOwner`}
            />
          </NotApplicable>
        </Field>

        <Branch
          attribute={`${attribute}.HadSuccess`}
          name="system-success"
          label={i18n.t(`${pvqPrefix}.heading.systemsuccess`)}
          labelSize="h4"
          {...this.props.HadSuccess}
          onUpdate={this.updateHadSuccess}
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.HadSuccess?.value === 'Yes' && (
        <>
          <Field
            title={i18n.t(`${pvqPrefix}.heading.infotype`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SystemOwner`}
            exactAttribute
          >

            <Dropdown
              attribute={`${attribute}.InformationType`}
              name="information-type"
              title={i18n.t(`${pvqPrefix}.heading.infotype`)}
              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateInformationType}
              value={this.props.InformationType.value}
              required={this.props.required}
            >
              {systemITTypes.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>

            {this.props.InformationType?.value === 'Other' && (

            <Textarea
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="system-other"
              attribute={`${attribute}.InformationTypeOtherExplanation`}
              {...this.props.InformationTypeOtherExplanation}
              onUpdate={this.updateInformationTypeOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />

            )}
          </Field>
          <Field
            title={i18n.t(`${pvqPrefix}.heading.systemtimes`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SystemOwner`}
            exactAttribute
          >
            <Dropdown
              attribute={`${attribute}.Frequency`}
              name="frequency"

              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateFrequency}
              value={this.props.Frequency.value}
              required={this.props.required}
            >
              {handingProtectedInformationFrequencyOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>
            {this.props.Frequency?.value === 'Other' && (

            <Textarea
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="frequency-explain"
              attribute={`${attribute}.FrequencyOtherExplanation`}
              {...this.props.FrequencyOtherExplanation}
              onUpdate={this.updateFrequencyOtherExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />

            )}
          </Field>
          <Field
            title={i18n.t(`${pvqPrefix}.heading.systemoccurances`)}
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.SystemOwner`}
            exactAttribute
          >
            <Dropdown
              attribute={`${attribute}.Occurances`}
              name="sys-occurancnces"

              onError={this.handleError}
              onFocus={this.handleFocus}
              onBlur={this.handleBlur}
              onUpdate={this.updateOccurances}
              value={this.props.Occurances.value}
              required={this.props.required}
            >
              {handingProtectedInformationFrequencyOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Dropdown>
            {this.props.Occurances?.value === 'Other' && (
            <Textarea

              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="sys-occurancnces_expl"
              attribute={`${attribute}.OccurancesExplanation`}
              {...this.props.OccurancesExplanation}
              onUpdate={this.updateOccurancesExplanation}
              onError={this.props.onError}
              required={this.props.required}
            />

            )}
          </Field>

          <Field
            attribute={`${attribute}.Reason`}
            scrollIntoView={this.props.scrollIntoView}
            title={i18n.t(`${pvqPrefix}.heading.systemwhy`)}
          >
            <Textarea
              title={i18n.t(`${pvqPrefix}.heading.systemwhy`)}
              name="reason"
              attribute={`${attribute}.Reason`}
              {...this.props.Reason}
              onUpdate={this.updateReason}
              onError={this.props.onError}
              required={this.props.required}
            />
          </Field>

          <Field
            attribute={`${attribute}.UnauthorizedActions`}
            title={i18n.t(`${pvqPrefix}.heading.systemget`)}
            adjustFor="p"
            scrollIntoView={this.props.scrollIntoView}
          >

            <Actions
              attribute={`${attribute}.UnauthorizedActions`}
              name="actions"
              className="incident-UnauthorizedActions"
              {...this.props.UnauthorizedActions}
              onUpdate={this.updateUnauthorizedActions}
              onError={this.props.onError}
              required={this.props.required}
            />
            <Textarea
              label={i18n.t(`${pvqPrefix}.heading.actiondetails`)}
              name="actions-details"
              attribute={`${attribute}.ActionDetails`}
              {...this.props.ActionDetails}
              onUpdate={this.updateActionDetails}
              onError={this.props.onError}
              required={this.props.required}
            />
            {actionother && (
            <Textarea
              label={i18n.t(`${pvqPrefix}.para.explain`)}
              name="details-other"
              attribute={`${attribute}.ActionDetailsOther`}
              {...this.props.ActionOtherDetails}
              onUpdate={this.updateActionOtherDetails}
              onError={this.props.onError}
              required={this.props.required}
            />
            )}
          </Field>
        </>

        )}

      </div>
    )
      : (
        <div>
          <Field
            title={i18n.t('legal.technology.manipulating.heading.date')}
            help="legal.technology.manipulating.help.date"
            adjustFor="datecontrol"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Date`}
          >
            <DateControl
              name="Date"
              {...this.props.Date}
              minDateEqualTo
              onUpdate={this.updateDate}
              onError={this.props.onError}
              className="legal-technology-manipulating-date"
              required={this.props.required}
              hideDay
              attribute={`${attribute}.Date`}
            />
          </Field>

          <Field
            title={i18n.t('legal.technology.manipulating.heading.incident')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Incident`}
          >
            <Textarea
              name="Incident"
              {...this.props.Incident}
              onUpdate={this.updateIncident}
              onError={this.props.onError}
              className="legal-technology-manipulating-incident"
              required={this.props.required}
              attribute={`${attribute}.Incident`}
            />
          </Field>

          <Field
            title={i18n.t('legal.technology.manipulating.heading.location')}
            optional
            help="legal.technology.manipulating.help.location"
            adjustFor="address"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Location`}
          >
            <Location
              name="Location"
              {...this.props.Location}
              onUpdate={this.updateLocation}
              onError={this.props.onError}
              className="legal-technology-manipulating-location"
              layout={Location.ADDRESS}
              geocode
              showPostOffice
              addressBooks={this.props.addressBooks}
              addressBook="Incident"
              dispatch={this.props.dispatch}
              required={this.props.required}
              attribute={`${attribute}.Location`}
            />
          </Field>

          <Field
            title={i18n.t('legal.technology.manipulating.heading.action')}
            adjustFor="textarea"
            scrollIntoView={this.props.scrollIntoView}
            attribute={`${attribute}.Action`}
          >
            <Textarea
              name="Action"
              {...this.props.Action}
              onUpdate={this.updateAction}
              onError={this.props.onError}
              className="legal-technology-manipulating-action"
              required={this.props.required}
              attribute={`${attribute}.Action`}
            />
          </Field>
        </div>
      );
  }
}

ManipulatingItem.defaultProps = {
  onUpdate: (queue) => {},
  onError: (value, arr: ErrorCode) => arr,
  // PVQ Fields 18
  ManipulatingLocations: {},
  SystemName: {},
  SystemOwner: {},
  SystemOwnerNotApplicable: { applicable: true },
  HadSuccess: {},
  InformationType: {},
  InformationTypeOtherExplanation: {},
  Frequency: {},
  FrequencyOtherExplanation: {},
  Occurances: {},
  OccurancesExplanation: {},
  UnauthorizedActions: [],
  ActionDetails: {},
  ActionDetailsOther: {},
  Reason: {},
};
